import produce from 'immer';

import type { Loading, PageData, UILayout } from '@swf/core/types/Poker';
import type {
  GetFilteredClubInfoResponse,
  GetUserClubsAndScheduledGamesResponse,
  GetUserClubsResponse,
  PlayerListElement,
  RecentTournament
} from '@swf/generated/types/api/v0/SwHomeGames';
import { PKR_INITIAL_STATE, sortClubs } from '@swf/poker';

export interface GameSelect {
  gameId: string;
  select: boolean;
}

export { setClubMembers } from './reducers/clubMembers';
export { setClubOptions } from './reducers/clubOptions';
export { setClubSeasons } from './reducers/clubSeasons';
export { setClubSeasonStats } from './reducers/clubSeasonStats';
export { setError } from './reducers/error';
export { setPlayerTournamentResults } from './reducers/playerTournamentResults';
export { setRecentTournamentSummary } from './reducers/recentTournamentSummary';
export { setRegisteredTournaments } from './reducers/registeredTournaments';

const filterTournaments = (tourns: RecentTournament[] | undefined) => {
  const initialState = {
    available: [] as RecentTournament[],
    recent: [] as RecentTournament[]
  };

  if (!tourns || !tourns.length) return initialState;

  return tourns.reduce((acc, tourn) => {
    if (!tourn || !tourn.dynamicData) {
      return acc;
    }

    const { status } = tourn.dynamicData;

    if (
      status === 'MT_STATUS_PUBLISHED' ||
      status === 'MT_STATUS_SEATS_AVAIL' ||
      status === 'MT_STATUS_TOURN_STARTED'
    ) {
      acc.available.push(tourn);
    } else {
      acc.recent.push(tourn);
    }

    return acc;
  }, initialState);
};

export const setUserClubs = (state = PKR_INITIAL_STATE, payload: GetUserClubsResponse) =>
  produce(state, (draft) => {
    draft.clubs = sortClubs({ clubs: payload?.userClubs?.clubInfoList, prevClubs: state.clubs });
  });

export const setClubInfo = (
  state = PKR_INITIAL_STATE,
  { filteredClubInfoReply }: GetFilteredClubInfoResponse
) => {
  const { available, recent } = filterTournaments(filteredClubInfoReply?.tourns);

  return produce(state, (draft) => {
    draft.games.tables = filteredClubInfoReply?.tables;
    draft.games.tourns = available;
    draft.games.recentTourns = recent;
  });
};

export const setUserClubsAndGames = (
  state: PageData | undefined = PKR_INITIAL_STATE,
  { clubInfoList, filteredClubInfoReply }: GetUserClubsAndScheduledGamesResponse
) => {
  const { available, recent } = filterTournaments(filteredClubInfoReply?.tourns);

  return produce(state, (draft) => {
    draft.clubs = sortClubs({ clubs: clubInfoList, prevClubs: state.clubs });
    draft.games.tables = filteredClubInfoReply?.tables;
    draft.games.tourns = available;
    draft.games.recentTourns = recent;
  });
};

export const setLoading = (state: PageData, { loaderName, isLoading }: Loading) =>
  produce(state, (draft) => {
    draft.loading[loaderName] = isLoading;
  });

export const setUiLayout = (
  state: PageData,
  { key, value }: { key: keyof UILayout; value: boolean }
) =>
  produce(state, (draft) => {
    draft.uiLayout[key] = value;
  });

export const reset = (state: PageData) => produce(state, (draft) => PKR_INITIAL_STATE);

export const gameSelect = (state: PageData, { gameId, select }: GameSelect) =>
  produce(state, (draft) => {
    if (select) {
      draft.games.selectedGameIds = [gameId];
    } else {
      const index = state.games.selectedGameIds.indexOf(gameId);

      draft.games.selectedGameIds.splice(index, 1);
    }
  });

export const setPlayersByClubId = (
  state: PageData,
  { clubId, response }: { clubId: string | number; response: PlayerListElement[] }
) =>
  produce(state, (draft) => {
    draft.playersByClubId[clubId] = response;
  });
